import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { coursesAPI, paymentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchased, setPurchased] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await coursesAPI.getById(id);
      setCourse(response.data.data);
      
      if (isAuthenticated) {
        checkPurchaseStatus();
      }
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const response = await coursesAPI.checkPurchased(id);
      setPurchased(response.data.data.purchased);
    } catch (error) {
      console.error('Error checking purchase status:', error);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setPurchaseLoading(true);
    try {
      const response = await paymentsAPI.initiate({
        course_id: parseInt(id),
        amount: course.price
      });

      if (response.data.success) {
        // Redirect to Easebuzz payment gateway
        window.location.href = response.data.data.payment_url;
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      alert('Payment initiation failed. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  if (loading) {
    return <div className="pt-32 text-center text-earth-500">Loading...</div>;
  }

  if (!course) {
    return <div className="pt-32 text-center text-earth-500">Course not found.</div>;
  }

  return (
    <div className="pt-32 pb-16 px-4 max-w-3xl mx-auto bg-background min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="bg-card border border-earth-200 rounded-2xl overflow-hidden"
      >
        <div className="relative h-64 overflow-hidden">
          <img
            src={course.image_url}
            alt={course.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = `https://via.placeholder.com/600x400/FF6B35/FFFFFF?text=${encodeURIComponent(course.title)}`;
            }}
          />
          <div className="absolute top-4 left-4 bg-saffron-400 text-xs px-4 py-2 rounded-full text-white font-semibold">
            {course.category}
          </div>
        </div>
        <div className="p-8">
          <h1 className="text-3xl font-serif font-bold mb-2 text-earth-900">{course.title}</h1>
          <p className="text-earth-700 mb-4 text-lg">{course.description}</p>
          <p className="text-earth-800 mb-6">{course.details}</p>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-semibold text-saffron-600">
              ₹{course.price}
            </span>
            {purchased ? (
              <button
                disabled
                className="px-6 py-3 rounded-lg bg-green-100 text-green-700 border border-green-300 text-lg font-medium cursor-not-allowed"
              >
                ✓ Purchased
              </button>
            ) : (
              <button
                onClick={handlePurchase}
                disabled={purchaseLoading}
                className="px-6 py-3 rounded-lg border border-saffron-400 text-saffron-600 hover:bg-saffron-50 transition-colors text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchaseLoading ? 'Processing...' : 'Buy Now'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CourseDetails; 